<template>
  <div>
    <div class="card">
      <div class="card-body row">
        <div class="col-md-6">
          <form method="post" ref="form" @submit.prevent="validateAndSubmit">
            <div>
              <h4>
                <small>
                  <i class="fa fa-circle text-cr-red-black"></i>
                </small>
                {{ $t('word.invoice_data') }}
              </h4>
              <div class="mb-2">
                <v-text-input
                  v-if="!organisation.code"
                  id="research-submit-organisation-code"
                  v-model="formData.code"
                  :errors="errors.code"
                  :required="true"
                  label="word.coc"
                  :max-length="8"
                />
                <div v-else>
                  <strong>{{ $t('word.coc') }}:</strong><br/>
                  {{ organisation.code }}
                </div>
              </div>

              <div class="mb-2">
                <v-text-input
                  id="research-submit-address"
                  v-model="formData.address"
                  :errors="errors.address"
                  :required="true"
                  label="word.address"
                />
              </div>

              <div class="mb-2">
                <v-text-input
                  id="research-submit-zip"
                  :required="true"
                  label="word.zip"
                  v-model="formData.zip"
                  :errors="errors.zip"
                />
              </div>

              <div class="mb-2">
                <v-text-input
                  id="research-submit-city"
                  v-model="formData.city"
                  :errors="errors.city"
                  :required="true"
                  label="word.city"
                />
              </div>

              <hr/>
            </div>

            <h4>
              <small>
                <i class="fa fa-circle text-cr-red-black"></i>
              </small>
              {{ $t('phrase.remarks') }}
            </h4>
            <v-text-area
              id="research-form-remarks"
              v-model="formData.remarks"
              :errors="errors.remarks"
            />
          </form>
        </div>
        <div class="col-md-6" v-if="researchType.initial_price">
          <h4>
            <small>
              <i class="fa fa-circle text-cr-red-black"></i>
            </small>
            {{ $t('word.overview') }}
          </h4>
          <table class="table">
            <tr>
              <td>
                <strong>
                  {{ $t('word.package') }} ({{ researchType.name }})
                </strong>
              </td>
              <td class="text-right" id="research-form-price-initial">
                &euro; {{ researchType.initial_price }},00
              </td>
            </tr>
            <tr>
              <td>
                <strong>
                  {{ $t('phrase.extra_questions') }}
                </strong>
                <div class="badge badge-pill badge-info">
                  {{ addedResearchItemsCount }}
                </div>
              </td>
              <td class="text-right" id="research-form-price-extra">
                &euro; {{ addedResearchItemsCount * 50 }},00
              </td>
            </tr>
            <tr>
              <td :class="{'text-lighter': !formData.join_competition}">
                <strong>
                  {{ $t('word.competition') }}
                </strong>
              </td>
              <td class="text-right" id="research-form-price-competition">
                <span v-if="formData.join_competition">
                  &euro; 100,00
                </span>
                <span v-else>
                  -
                </span>
              </td>
            </tr>

            <tr>
              <td><strong>{{ $t('word.total') }}</strong></td>
              <td class="text-right text-bold" id="research-form-price-total">
                &euro;
                <v-animated-number
                  :value="totalPrice.toFixed(0)"
                  :formatValue="(value) => (value.toFixed(0))"
                  :duration="500"
                />,00
              </td>
            </tr>
          </table>

          <hr />
          <div
            class="card card-body bg-competition"
            v-if="researchType.suggest_competition"
          >
            <h4>
              <small>
                <i class="fa fa-circle text-cr-red-black"></i>
              </small>
              {{ $t('phrase.join_competition') }}
            </h4>

            {{ $t('text.join_competition') }}
            <hr/>
            <label>
              <input
                type="checkbox"
                v-model="formData.join_competition"
              />
              <em class="pl-2">{{ $t('sentence.join_competition') }}</em>
            </label>
          </div>

          <div>
            <hr/>
            <v-check-box
              :label="$t('sentence.accept_price', { totalPrice: totalPrice })"
              :translate-text="false"
              v-model="conditionsAccepted"
              :errors="errors.conditionsAccepted"
              id="register-research-accept-terms"
            />
          </div>
        </div>
      </div>
      <div class="card-footer d-flex justify-content-end">
        <div class="mr-2">
          <v-button
            text="word.back"
            id="research-submit-back-button"
            icon="chevron-left"
            style-type="secondary"
            @click="$emit('back')"
          />
        </div>
        <div>
          <v-button
            id="research-submit-submit-button"
            text="word.submit"
            icon="check"
            style-type="primary"
            @click="validateAndSubmit"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AnimatedNumber from 'animated-number-vue';
import Button from '@/common/components/Button.vue';
import CheckBox from '@/common/components/inputs/CheckBox.vue';
import TextArea from '@/common/components/inputs/TextArea.vue';
import TextInput from '@/common/components/inputs/TextInput.vue';
import { toPrettyPrice } from '@/common/utils/formatting';
import { submitResearch } from '@/organisation/api';
import { notify } from '@/common/utils/notify';

export default {
  name: 'ResearchSubmit',
  components: {
    'v-animated-number': AnimatedNumber,
    'v-check-box': CheckBox,
    'v-text-area': TextArea,
    'v-text-input': TextInput,
    'v-button': Button,
  },
  props: {
    research: {
      type: Object,
      required: true,
    },
    organisation: {
      type: Object,
      required: true,
    },
    researchType: {
      type: Object,
      required: true,
    },
    totalPrice: {
      type: Number,
      required: false,
    },
    addedResearchItemsCount: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      conditionsAccepted: false,
      errors: {},
      formData: {
        code: '',
        address: '',
        zip: '',
        city: '',
        remarks: '',
        join_competition: false,
      },
    };
  },
  watch: {
    joinedCompetition() {
      this.$emit('joined-competition', this.joinedCompetition);
    },
  },
  computed: {
    joinedCompetition() {
      return this.formData.join_competition;
    },
  },
  methods: {
    toPrettyPrice,
    validate() {
      let isValid = true;
      this.errors = {};
      if (!this.organisation.code) {
        if (!this.formData.code) {
          this.errors = {
            ...this.errors,
            code: [this.$t('phrase.field_required')],
          };
          isValid = false;
        } else if (!this.formData.code.match(/^[0-9]+$/)) {
          this.errors = {
            ...this.errors,
            code: [this.$t('phrase.coc_digits_only')],
          };
          isValid = false;
        } else if (this.formData.code.length !== 8) {
          this.errors = {
            ...this.errors,
            code: [this.$t('phrase.coc_max_8_digits', { length: 8 })],
          };
          isValid = false;
        }
      }
      if (!this.formData.address) {
        this.errors = {
          ...this.errors,
          address: [this.$t('phrase.field_required')],
        };
        isValid = false;
      }
      if (!this.formData.zip) {
        this.errors = {
          ...this.errors,
          zip: [this.$t('phrase.field_required')],
        };
        isValid = false;
      }
      if (!this.formData.city) {
        this.errors = {
          ...this.errors,
          city: [this.$t('phrase.field_required')],
        };
        isValid = false;
      }
      if (!this.conditionsAccepted) {
        this.errors.conditionsAccepted = [this.$t('phrase.field_required')];
        isValid = false;
      }

      return isValid;
    },
    async validateAndSubmit() {
      if (this.validate()) {
        const statusCode = await this.submit();
        if (statusCode === 200) {
          this.$emit('submitted');
        }
      }
      return false;
    },
    async submit() {
      const response = await submitResearch(
        this.organisation.slug, this.research.uuid, this.formData,
      );
      if (response.status === 200) {
        notify(this.$t('phrase.research_submitted'), this.$t('sentence.research_submitted'), 'success');
      } else {
        notify(this.$t('phrase.research_submit_failed'), this.$t('sentence.research_submit_failed'), 'error');
      }
      return response.status;
    },
  },
  created() {
    if (!this.researchType.initial_price) {
      this.conditionsAccepted = true;
    }
    this.formData = {
      ...this.formData,
      code: this.organisation.code,
      address: this.organisation.address,
      zip: this.organisation.zip,
      city: this.organisation.city,
    };
  },
};
</script>
